import React, { useState, useMemo, useCallback } from 'react';
import { useDb } from '../../db/useDataService';
import * as ds from '../../db/dataService';

import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
  ArrowLeftOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  DesktopOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  Button,
  Table,
  Tag,
  Space,
  Popconfirm,
  Tooltip,
  Typography,
  Empty,
  Input,
  message,
} from 'antd';
import { ColumnsType } from 'antd/lib/table';

import {
  Campaign,
  CampaignStatus,
  AdGroup,
  CampaignAd,
  CAMPAIGN_STATUS_CONFIG,
  PLACEMENT_CONFIG,
  PAYMENT_MODEL_CONFIG,
  GROUPING_LEVEL_CONFIG,
  AngleType,
  ANGLE_TYPE_CONFIG,
  AdSitelink,
  LifeSphere,
  SmOffer,
  AdCabinet,
} from '../../types';

import CampaignDetail from './CampaignDetail';
import CreateCampaignModal from './CreateCampaignModal';
import DuplicateCampaignModal from './DuplicateCampaignModal';

const { Title, Text } = Typography;

// --- Mock data ---

export const mockCampaigns: Campaign[] = [
  {
    id: 'c1',
    productId: '1',
    name: 'Похудение — Поиск',
    platform: 'yandex_direct',
    cabinetId: '58',
    offerId: '56',
    placement: 'search',
    strategy: 'max_conversions',
    paymentModel: 'per_conversions',
    weeklyBudget: 35000,
    conversionPriceLimit: 1200,
    conversionName: 'lead_webinar',
    negativeKeywords: ['бесплатно', 'скачать', 'реферат', 'курсовая'],
    settings: {
      groupingLevel: 'theme',
      selectedItemIds: ['8', '9', '10'],
      adsPerGroup: 3,
      selectedAngles: ['pain', 'instruction', 'provocation'],
    },
    status: 'active',
    createdAt: '2026-03-15T10:00:00Z',
    createdBy: 'user1',
    clonedFromId: null,
  },
  {
    id: 'c2',
    productId: '1',
    name: 'Энергия и сон — Сети',
    platform: 'yandex_direct',
    cabinetId: '58',
    offerId: '56',
    placement: 'networks',
    strategy: 'max_conversions',
    paymentModel: 'per_clicks',
    weeklyBudget: 20000,
    conversionPriceLimit: null,
    conversionName: 'lead_webinar',
    negativeKeywords: [],
    settings: {
      groupingLevel: 'theme',
      selectedItemIds: ['11', '12'],
      adsPerGroup: 2,
      selectedAngles: ['dream', 'secret', 'contrast'],
    },
    status: 'draft',
    createdAt: '2026-04-01T14:00:00Z',
    createdBy: 'user1',
    clonedFromId: null,
  },
  {
    id: 'c3',
    productId: '1',
    name: 'Похудение — Поиск (кабинет 2)',
    platform: 'yandex_direct',
    cabinetId: '59',
    offerId: '56',
    placement: 'search',
    strategy: 'max_conversions',
    paymentModel: 'per_conversions',
    weeklyBudget: 35000,
    conversionPriceLimit: 1200,
    conversionName: 'lead_webinar',
    negativeKeywords: ['бесплатно', 'скачать'],
    settings: {
      groupingLevel: 'theme',
      selectedItemIds: ['8', '9', '10'],
      adsPerGroup: 3,
      selectedAngles: ['pain', 'instruction', 'provocation'],
    },
    status: 'paused',
    createdAt: '2026-04-05T09:00:00Z',
    createdBy: 'user1',
    clonedFromId: 'c1',
  },
];

const emptySitelink: AdSitelink = { title: '', description: '', url: '' };
const emptySitelinks: [AdSitelink, AdSitelink, AdSitelink, AdSitelink] = [emptySitelink, emptySitelink, emptySitelink, emptySitelink];

export const mockAdGroups: AdGroup[] = [
  { id: 'g1', campaignId: 'c1', sourceId: '8', sourceLevel: 'theme', name: 'Похудение', keywords: ['похудение', 'снижение веса', 'как похудеть'], suKeywords: ['похудеть к лету', 'все диеты перепробовала'], images: [], enabled: true },
  { id: 'g2', campaignId: 'c1', sourceId: '9', sourceLevel: 'theme', name: 'Набор массы', keywords: ['набор массы', 'как набрать вес'], suKeywords: ['ем много не могу набрать'], images: [], enabled: true },
  { id: 'g3', campaignId: 'c1', sourceId: '10', sourceLevel: 'theme', name: 'Удержание веса', keywords: ['удержание веса', 'вес вернулся'], suKeywords: ['скинула 10 кг вернулись', 'не могу удержать результат'], images: [], enabled: true },
  { id: 'g4', campaignId: 'c2', sourceId: '11', sourceLevel: 'theme', name: 'Хроническая усталость', keywords: ['хроническая усталость', 'нет сил'], suKeywords: ['просыпаюсь уставшей', 'после обеда хочу спать'], images: [], enabled: true },
  { id: 'g5', campaignId: 'c2', sourceId: '12', sourceLevel: 'theme', name: 'Сон', keywords: ['бессонница', 'не могу уснуть', 'нарушение сна'], suKeywords: ['не могу уснуть до 2 ночи'], images: [], enabled: true },
];

export const mockCampaignAds: CampaignAd[] = [
  { id: 'ca1', adGroupId: 'g1', campaignId: 'c1', unitId: '23', angleType: 'pain', title: 'До лета 3 месяца, а вы всё ещё +15 кг?', title2: 'Бесплатный вебинар', text: 'Узнайте как начать худеть без диет и срывов. Вебинар-практикум.', url: 'example.com/webinar', displayUrl: 'example.com/похудение', sitelinks: emptySitelinks, callouts: ['Бесплатно', 'Без диет'], actionButton: '', image: undefined, imageText: '', imageButtonText: '', status: 'active', variantIndex: 1 },
  { id: 'ca2', adGroupId: 'g1', campaignId: 'c1', unitId: '23', angleType: 'instruction', title: '5 шагов к похудению без диет', title2: 'Пошаговый план', text: 'Получите конкретный план питания на бесплатном вебинаре. Без подсчёта калорий.', url: 'example.com/webinar', displayUrl: 'example.com/план', sitelinks: emptySitelinks, callouts: ['5 шагов', 'Бесплатно'], actionButton: '', image: undefined, imageText: '', imageButtonText: '', status: 'active', variantIndex: 2 },
  { id: 'ca3', adGroupId: 'g1', campaignId: 'c1', unitId: '25', angleType: 'provocation', title: 'Диеты не работают. Хватит мучить себя', title2: 'Есть другой путь', text: 'Перестаньте считать калории. Система питания без срывов — на бесплатном вебинаре.', url: 'example.com/webinar', displayUrl: 'example.com/без-диет', sitelinks: emptySitelinks, callouts: ['Без подсчёта калорий', 'Скидка 40%'], actionButton: '', image: undefined, imageText: '', imageButtonText: '', status: 'draft', variantIndex: 3 },
  { id: 'ca4', adGroupId: 'g2', campaignId: 'c1', unitId: '26', angleType: 'pain', title: 'Ем за двоих, а вешу 50 кг?', title2: 'Узнайте причину', text: 'На вебинаре разберём причины дефицита массы тела и план набора.', url: 'example.com/webinar', displayUrl: 'example.com/набор', sitelinks: emptySitelinks, callouts: ['Бесплатно', 'Онлайн'], actionButton: '', image: undefined, imageText: '', imageButtonText: '', status: 'active', variantIndex: 1 },
  { id: 'ca5', adGroupId: 'g3', campaignId: 'c1', unitId: '27', angleType: 'pain', title: 'Скинули 10 кг — а они вернулись?', title2: 'Это поправимо', text: 'Как удержать результат без диет. Бесплатный вебинар по системе питания.', url: 'example.com/webinar', displayUrl: 'example.com/результат', sitelinks: emptySitelinks, callouts: ['Без возврата веса', 'Бесплатно'], actionButton: '', image: undefined, imageText: '', imageButtonText: '', status: 'active', variantIndex: 1 },
];

// --- Component ---

interface CampaignsTabProps {
  productId: string;
  spheres: LifeSphere[];
  offers: SmOffer[];
  cabinets: AdCabinet[];
}

const CampaignsTab: React.FC<CampaignsTabProps> = ({ productId, spheres, offers, cabinets }) => {
  const db = useDb();
  const campaigns = db.campaigns.filter((c) => c.productId === productId);
  const setCampaigns: React.Dispatch<React.SetStateAction<Campaign[]>> = (u) => {
    const next = typeof u === 'function' ? (u as (p: Campaign[]) => Campaign[])(campaigns) : u;
    ds.setCampaigns(productId, next);
  };
  const adGroups = db.adGroups;
  const setAdGroups: React.Dispatch<React.SetStateAction<AdGroup[]>> = (u) => {
    const next = typeof u === 'function' ? (u as (p: AdGroup[]) => AdGroup[])(adGroups) : u;
    ds.setAdGroups(next);
  };
  const campaignAds = db.campaignAds;
  const setCampaignAds: React.Dispatch<React.SetStateAction<CampaignAd[]>> = (u) => {
    const next = typeof u === 'function' ? (u as (p: CampaignAd[]) => CampaignAd[])(campaignAds) : u;
    ds.setCampaignAds(next);
  };
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [duplicateTarget, setDuplicateTarget] = useState<Campaign | null>(null);
  const [searchText, setSearchText] = useState('');

  const selectedCampaign = useMemo(() => campaigns.find((c) => c.id === selectedCampaignId) || null, [campaigns, selectedCampaignId]);

  const filteredCampaigns = useMemo(() => {
    if (!searchText) return campaigns;
    return campaigns.filter((c) => c.name.toLowerCase().includes(searchText.toLowerCase()));
  }, [campaigns, searchText]);

  const getGroupsCount = useCallback((campaignId: string) => adGroups.filter((g) => g.campaignId === campaignId).length, [adGroups]);
  const getAdsCount = useCallback((campaignId: string) => campaignAds.filter((a) => a.campaignId === campaignId).length, [campaignAds]);

  const handleDeleteCampaign = useCallback((id: string) => {
    setCampaigns((prev) => prev.filter((c) => c.id !== id));
    setAdGroups((prev) => prev.filter((g) => g.campaignId !== id));
    setCampaignAds((prev) => prev.filter((a) => a.campaignId !== id));
    message.success('Кампания удалена');
  }, []);

  const handleDuplicateConfirm = useCallback((campaign: Campaign, groups: AdGroup[], ads: CampaignAd[]) => {
    setCampaigns((prev) => [campaign, ...prev]);
    setAdGroups((prev) => [...prev, ...groups]);
    setCampaignAds((prev) => [...prev, ...ads]);
    setDuplicateTarget(null);
    message.success('Кампания дублирована');
  }, []);

  const handleToggleStatus = useCallback((id: string) => {
    setCampaigns((prev) => prev.map((c) => {
      if (c.id !== id) return c;
      const next: CampaignStatus = c.status === 'active' ? 'paused' : 'active';
      return { ...c, status: next };
    }));
  }, []);

  const getCabinetName = useCallback((cabinetId: string) => {
    return cabinets.find((c) => c.id === cabinetId)?.name || cabinetId;
  }, [cabinets]);

  const getOfferName = useCallback((offerId: string) => {
    return offers.find((o) => o.id === offerId)?.name || offerId;
  }, [offers]);

  // --- Detail view ---
  if (selectedCampaign) {
    const groups = adGroups.filter((g) => g.campaignId === selectedCampaign.id);
    const ads = campaignAds.filter((a) => a.campaignId === selectedCampaign.id);
    return (
      <CampaignDetail
        campaign={selectedCampaign}
        adGroups={groups}
        campaignAds={ads}
        cabinetName={getCabinetName(selectedCampaign.cabinetId)}
        offerName={getOfferName(selectedCampaign.offerId)}
        onBack={() => setSelectedCampaignId(null)}
      />
    );
  }

  // --- List view ---
  const columns: ColumnsType<Campaign> = [
    {
      title: 'Название',
      key: 'name',
      render: (_: unknown, record: Campaign) => (
        <div>
          <a onClick={() => setSelectedCampaignId(record.id)} style={{ fontWeight: 500, color: '#1890ff', cursor: 'pointer' }}>
            {record.name}
          </a>
          {record.clonedFromId && <Tag style={{ marginLeft: 6, fontSize: 10 }}>копия</Tag>}
        </div>
      ),
    },
    {
      title: 'Статус',
      key: 'status',
      width: 110,
      render: (_: unknown, record: Campaign) => {
        const cfg = CAMPAIGN_STATUS_CONFIG[record.status];
        return <Tag color={cfg.color}>{cfg.label}</Tag>;
      },
    },
    {
      title: 'Размещение',
      key: 'placement',
      width: 130,
      render: (_: unknown, record: Campaign) => PLACEMENT_CONFIG[record.placement],
    },
    {
      title: 'Группы',
      key: 'groups',
      width: 80,
      align: 'center',
      render: (_: unknown, record: Campaign) => <strong>{getGroupsCount(record.id)}</strong>,
    },
    {
      title: 'Объявления',
      key: 'ads',
      width: 100,
      align: 'center',
      render: (_: unknown, record: Campaign) => <strong>{getAdsCount(record.id)}</strong>,
    },
    {
      title: 'Бюджет/нед.',
      key: 'budget',
      width: 120,
      align: 'right',
      render: (_: unknown, record: Campaign) => `${record.weeklyBudget.toLocaleString('ru-RU')} ₽`,
    },
    {
      title: 'Кабинет',
      key: 'cabinet',
      width: 180,
      render: (_: unknown, record: Campaign) => (
        <span><DesktopOutlined style={{ marginRight: 4 }} />{getCabinetName(record.cabinetId)}</span>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 150,
      render: (_: unknown, record: Campaign) => (
        <Space size={4}>
          <Tooltip title={record.status === 'active' ? 'Пауза' : 'Запустить'}>
            <Button
              type="text"
              size="small"
              icon={record.status === 'active' ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
              onClick={() => handleToggleStatus(record.id)}
              disabled={record.status === 'archived'}
            />
          </Tooltip>
          <Tooltip title="Дублировать">
            <Button type="text" size="small" icon={<CopyOutlined />} onClick={() => setDuplicateTarget(record)} />
          </Tooltip>
          <Tooltip title="Удалить">
            <Popconfirm title="Удалить кампанию?" onConfirm={() => handleDeleteCampaign(record.id)} okText="Да" cancelText="Нет">
              <Button type="text" size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="campaigns-tab">
      <div className="campaigns-tab__header">
        <Title level={5} style={{ margin: 0 }}>Кампании</Title>
        <div className="campaigns-tab__header-actions">
          <Input
            placeholder="Поиск"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
            style={{ width: 200 }}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateModalVisible(true)}
            style={{ backgroundColor: '#ff4d4f', borderColor: '#ff4d4f', borderRadius: 8 }}
          >
            Создать кампанию
          </Button>
        </div>
      </div>

      {filteredCampaigns.length === 0 ? (
        <Empty description="Нет кампаний" style={{ padding: '48px 0' }} />
      ) : (
        <Table
          rowKey="id"
          columns={columns}
          dataSource={filteredCampaigns}
          pagination={false}
          size="small"
          className="campaigns-tab__table"
        />
      )}

      <CreateCampaignModal
        visible={createModalVisible}
        onClose={() => setCreateModalVisible(false)}
        onCreate={(newCampaigns, groups, ads) => {
          setCampaigns((prev) => [...newCampaigns, ...prev]);
          setAdGroups((prev) => [...prev, ...groups]);
          setCampaignAds((prev) => [...prev, ...ads]);
          setCreateModalVisible(false);
          message.success(newCampaigns.length > 1
            ? `Создано ${newCampaigns.length} кампаний`
            : 'Кампания создана');
        }}
        productId={productId}
        spheres={spheres}
        offers={offers}
        cabinets={cabinets}
      />

      <DuplicateCampaignModal
        visible={!!duplicateTarget}
        campaign={duplicateTarget}
        adGroups={adGroups}
        campaignAds={campaignAds}
        cabinets={cabinets}
        offers={offers}
        onClose={() => setDuplicateTarget(null)}
        onDuplicate={handleDuplicateConfirm}
      />
    </div>
  );
};

export default CampaignsTab;
